"use client";
import { LogOut, Settings, X } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslations } from "next-intl";
import { domId } from "@/lib/dom-id";
import type { User } from "@/lib/client-auth";
import {
  Button,
  Avatar,
  AvatarFallback,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui";

import { useUserStore } from "@/store/user/user-store";
import { userProfileSelectors } from "@/store/user/slices/auth/selectors";
import {
  getProfilePictureValue,
  getColorByFirstLetter,
  normalizeProfilePictureUrl,
} from "@/utils/profile-utils";
import { cn } from "@/lib/utils";

function useSafeProfilePicture(pictureUrl?: string) {
  const [loadedUrl, setLoadedUrl] = useState<string>();

  useEffect(() => {
    if (!pictureUrl) return;

    let active = true;
    const image = new window.Image();
    image.referrerPolicy = "no-referrer";
    image.onload = () => active && setLoadedUrl(pictureUrl);
    image.onerror = () => active && setLoadedUrl(undefined);
    image.src = pictureUrl;

    return () => {
      active = false;
      image.onload = null;
      image.onerror = null;
    };
  }, [pictureUrl]);

  return pictureUrl && loadedUrl === pictureUrl ? pictureUrl : undefined;
}

function getDisplayName(user: User | null | undefined, fallback: string) {
  return (
    user?.name?.trim() ||
    user?.user_profile?.display_name?.trim() ||
    user?.display_name?.trim() ||
    fallback
  );
}

function UserAvatar({
  user,
  className,
  onClick,
}: {
  user: User | null | undefined;
  className?: string;
  onClick?: () => void;
}) {
  const displayName = getDisplayName(user, "U");
  const backgroundColor = getColorByFirstLetter(displayName);
  const pictureUrl = normalizeProfilePictureUrl(getProfilePictureValue(user));
  const loadedPictureUrl = useSafeProfilePicture(pictureUrl);
  return (
    <Avatar
      className={cn("h-8 w-8 cursor-pointer", className)}
      onClick={onClick}
    >
      {loadedPictureUrl ? (
        // The URL was preloaded only after applying no-referrer; Next Image cannot accept arbitrary Account hosts.
        // eslint-disable-next-line @next/next/no-img-element
        <img
          data-slot="avatar-image"
          className="aspect-square size-full object-cover"
          referrerPolicy="no-referrer"
          src={loadedPictureUrl}
          alt={displayName || "User"}
        />
      ) : (
        <AvatarFallback style={{ backgroundColor, color: "#fff" }}>
          {displayName[0]?.toUpperCase() || "U"}
        </AvatarFallback>
      )}
    </Avatar>
  );
}

export function UserMenuDropdown() {
  const t = useTranslations("UserMenu");
  const user = useUserStore(userProfileSelectors.userProfile);
  const { logout } = useUserStore();
  const ACCOUNT_INFO_URL_HOST = process.env.NEXT_PUBLIC_ACCOUNT_API_HOST;
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const displayName = getDisplayName(user, t("defaultName"));

  return (
    <DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
      <DropdownMenuTrigger data-testid={domId("btn", "user-menu-trigger")}>
        <UserAvatar user={user} />
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="min-w-[230px] pt-6 px-0 pb-0 relative z-100 border-none shadow-lg"
      >
        {/* Close button */}
        <div className="absolute top-1 right-2">
          <Button
            data-testid={domId("btn", "user-menu-close")}
            variant="ghost"
            className="w-[26px] h-[26px] p-0 rounded-full"
            onClick={() => setDropdownOpen(false)}
            aria-label={t("close")}
          >
            <X className="h-[13px] w-[13px] text-gray-500" />
          </Button>
        </div>

        {/* User Profile Section */}
        <div className="flex flex-col items-center px-6 pb-4 pt-0">
          <UserAvatar
            user={user}
            className="h-20 w-20 shrink-0 mb-2.5 text-2xl"
          />
          <h3 className="font-semibold text-sm text-gray-900 mb-1 truncate max-w-[150px]">
            {t("greeting")}, {displayName}!
          </h3>
          <p className="text-xs text-gray-500 truncate max-w-[150px]">
            {user?.email || user?.id || ""}
          </p>
        </div>

        <div className="px-4 pb-4">
          <DropdownMenuItem
            data-testid={domId("lnk", "user-menu-manage-account")}
            className="cursor-pointer px-3 py-[10.5px] h-10 rounded-lg mb-2"
            onClick={() => window.open(`${ACCOUNT_INFO_URL_HOST}`, "_blank")}
          >
            <Settings className="h-4 w-4 text-gray-600" />
            <span className="text-gray-700 whitespace-nowrap">
              {t("manageAccount")}
            </span>
          </DropdownMenuItem>
          {/* Sign out in bordered container */}
          <div className="border border-gray-200 rounded-lg cursor-pointer overflow-hidden">
            <DropdownMenuItem
              data-testid={domId("btn", "user-menu-sign-out")}
              onClick={() => {
                logout();
              }}
              className="cursor-pointer h-10 p-3 rounded-none"
            >
              <LogOut className="h-4 w-4 text-gray-600" strokeWidth={2} />
              <span className="text-gray-700">{t("signOut")}</span>
            </DropdownMenuItem>
          </div>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
